import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

export default function Gallery({ content }) {
  const items = content.gallery || [];
  const [active, setActive] = useState(null); // index or null
  const [filter, setFilter] = useState("todos");

  const cats = [
    { id: "todos", label: "Todos" },
    { id: "platos", label: "Platos" },
    { id: "mariscos", label: "Mariscos" },
    { id: "postres", label: "Postres" },
    { id: "bebidas", label: "Bebidas" },
  ];
  const visible = filter === "todos" ? items : items.filter((g) => (g.category || "platos") === filter);
  const availableCats = cats.filter((c) => c.id === "todos" || items.some((g) => (g.category || "platos") === c.id));

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
      if (active !== null && e.key === "ArrowRight") setActive((i) => (i + 1) % visible.length);
      if (active !== null && e.key === "ArrowLeft") setActive((i) => (i - 1 + visible.length) % visible.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, visible.length]);

  if (!items.length) return null;

  return (
    <section id="galeria" className="relative py-24 md:py-36 bg-[#f9f6f0]">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="mb-14">
          <p className="text-[#da251d] uppercase tracking-[0.3em] text-xs font-semibold mb-4">Galería</p>
          <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-none">
            Un vistazo a nuestra cocina
          </h2>
          <p className="mt-4 text-[#57534e] text-sm">Toca cualquier foto para verla en grande.</p>
        </div>

        <div className="flex flex-wrap gap-2.5 mb-10" data-testid="gallery-filters">
          {availableCats.map((c) => (
            <button
              key={c.id}
              onClick={() => { setFilter(c.id); setActive(null); }}
              data-testid={`gallery-filter-${c.id}`}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors duration-200 ${
                filter === c.id ? "bg-[#da251d] text-white" : "bg-white text-[#57534e] border border-stone-200 hover:border-[#da251d]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 auto-rows-[220px] md:auto-rows-[300px]">
          {visible.map((g, i) => (
            <motion.button
              type="button"
              key={g.id}
              onClick={() => setActive(i)}
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative overflow-hidden bg-stone-200 cursor-zoom-in ${i === 0 ? "col-span-2 row-span-2" : ""}`}
              data-testid={`gallery-item-${i}`}
            >
              <img
                src={g.image}
                alt={g.caption || "Foto"}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              {g.caption && (
                <figcaption className="absolute bottom-0 left-0 right-0 p-4 text-left text-white font-display text-lg md:text-xl translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  {g.caption}
                </figcaption>
              )}
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && visible[active] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            data-testid="gallery-lightbox"
          >
            <button
              onClick={() => setActive(null)}
              className="absolute top-5 right-5 text-white/80 hover:text-white z-10"
              data-testid="lightbox-close"
              aria-label="Cerrar"
            >
              <X size={32} />
            </button>
            <motion.figure
              key={visible[active].id}
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl w-full"
            >
              <img
                src={visible[active].image}
                alt={visible[active].caption || "Foto"}
                className="w-full max-h-[80vh] object-contain rounded-lg"
              />
              {visible[active].caption && (
                <figcaption className="mt-4 text-center text-white font-display text-xl md:text-2xl">
                  {visible[active].caption}
                </figcaption>
              )}
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
